import { useState, useEffect, useRef } from 'react';
import { plans as staticPlans } from '../data/landingPage/pricingContent';
import {
  fetchPublicPlans,
  getCachedPublicPlans,
} from '../utils/plansApi';import { mapApiPlanToPricingCard } from '../utils/pricingPlanIcons';
import {
  buildAnnualToggleSavingsLabel,
  inferSavingsPercentFromPrices,
} from '../utils/publicPlans';

function mapPayloadToCards(apiPlans) {
  return apiPlans.map((plan, index) => mapApiPlanToPricingCard(plan, index));
}

function buildStaticMeta(plans) {
  const savingsPercents = plans
    .map((plan) => inferSavingsPercentFromPrices(plan.monthlyPrice, plan.annualPrice))
    .filter((value) => value > 0);
  const annualSavingsPercent = savingsPercents.length ? Math.max(...savingsPercents) : 0;
  const annualSavingsPercentMin = savingsPercents.length ? Math.min(...savingsPercents) : 0;

  return {
    annualSavingsPercent,
    annualSavingsPercentMin,
    hasVariableAnnualSavings:
      savingsPercents.length > 1 && annualSavingsPercentMin !== annualSavingsPercent,
  };
}

function resolveInitialState() {
  const cached = getCachedPublicPlans();
  if (cached?.plans?.length) {
    return {
      plans: mapPayloadToCards(cached.plans),
      annualSavingsPercent: cached.annualSavingsPercent,
      annualSavingsPercentMin: cached.annualSavingsPercentMin,
      hasVariableAnnualSavings: cached.hasVariableAnnualSavings,
      fromApi: true,
    };
  }

  const staticMeta = buildStaticMeta(staticPlans);
  return {
    plans: staticPlans,
    ...staticMeta,
    fromApi: false,
  };
}

/**
 * Public pricing plans: instant paint (cache or static), background refresh from API.
 */
export default function usePublicPlans() {
  const hadCacheOnMount = useRef(null);
  if (hadCacheOnMount.current === null) {
    hadCacheOnMount.current = Boolean(getCachedPublicPlans()?.plans?.length);
  }

  const initial = resolveInitialState();
  const [plans, setPlans] = useState(() => initial.plans);
  const [annualSavingsPercent, setAnnualSavingsPercent] = useState(
    () => initial.annualSavingsPercent
  );
  const [annualSavingsPercentMin, setAnnualSavingsPercentMin] = useState(
    () => initial.annualSavingsPercentMin
  );
  const [hasVariableAnnualSavings, setHasVariableAnnualSavings] = useState(
    () => initial.hasVariableAnnualSavings
  );

  useEffect(() => {
    let cancelled = false;
    const hadCachedPlans = hadCacheOnMount.current;

    (async () => {
      try {
        const payload = await fetchPublicPlans({ forceRefresh: true });
        if (cancelled) return;
        if (payload.plans.length > 0) {
          setPlans(mapPayloadToCards(payload.plans));
          setAnnualSavingsPercent(payload.annualSavingsPercent);
          setAnnualSavingsPercentMin(payload.annualSavingsPercentMin);
          setHasVariableAnnualSavings(payload.hasVariableAnnualSavings);
          return;
        }
        // API returned no active plans — do not keep showing deleted/stale pricing cards.
        if (hadCachedPlans) {
          setPlans([]);
        }
      } catch {
        // Keep cache or static plans already on screen
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
  const annualToggleSavingsLabel = buildAnnualToggleSavingsLabel({
    annualSavingsPercent,
    hasVariableAnnualSavings,
  });

  return {
    plans,
    annualSavingsPercent,
    annualSavingsPercentMin,
    hasVariableAnnualSavings,
    annualToggleSavingsLabel,
  };
}
